import { useAllProductsContext } from '@app/components/products/context/AllProductsContext';
import { DatePicker } from '@app/components/various/DatePicker';
import { getDateRangeFormatter } from '@app/utils/dates';
import { useTranslationsConfig } from '@app/utils/hooks';
import { isDefined } from '@app/utils/is';
import { DatePickerNs, Dates } from '@typings';
import dayjs, { Dayjs } from 'dayjs';
import { omit } from 'ramda';
import React from 'react';

interface Props {
  title?: string;
}

export const EtaFilter = ({ title }: Props) => {
  const { changeFilters, filters, productsEtaDates } = useAllProductsContext();
  const { currentLanguageCode } = useTranslationsConfig();
  const { onlyAvailable, etaFrom, etaTo } = filters;
  const hasAppliedArrivingSoonFilter = onlyAvailable === 'arrivingSoon';
  const hasAppliedEtaFilter = isDefined(etaFrom) && isDefined(etaTo);

  React.useEffect(() => {
    if (hasAppliedEtaFilter && !hasAppliedArrivingSoonFilter) {
      changeFilters(omit(['etaFrom', 'etaTo'], filters));
    }
  }, [filters, changeFilters, hasAppliedArrivingSoonFilter, hasAppliedEtaFilter]);

  const datePickerValue: DatePickerNs.Value = React.useMemo(() => {
    if (!hasAppliedEtaFilter) {
      return [null, null];
    }

    return [dayjs(etaFrom), dayjs(etaTo)];
  }, [etaFrom, etaTo, hasAppliedEtaFilter]);

  const getShouldShowDot = (date: Dayjs, view: DatePickerNs.View) => {
    return productsEtaDates.some(etaDate => date.isSame(etaDate, view));
  };

  const handleDatePickerChange = React.useCallback(
    ([from, to]: DatePickerNs.RangeValue) => {
      if (isDefined(from) && !isDefined(to)) {
        return;
      }

      changeFilters({
        etaFrom: from?.format('YYYY-MM-DD') as Dates.DateAsHyphenSeparatedString,
        etaTo: to?.format('YYYY-MM-DD') as Dates.DateAsHyphenSeparatedString,
      });
    },
    [changeFilters],
  );

  if (!hasAppliedArrivingSoonFilter) {
    return null;
  }

  return (
    <label className="mt-2 block">
      <span className="sr-only">{title}</span>
      <DatePicker
        value={datePickerValue}
        triggerFormatter={getDateRangeFormatter(currentLanguageCode)}
        getShouldShowDot={getShouldShowDot}
        onChange={handleDatePickerChange}
        titleMobile={title}
      />
    </label>
  );
};
